import * as React from 'react'
import { useEffect, useState, useCallback } from 'react'
import { DefaultLayout } from '../layouts/DefaultLayout'
import { useStoryblokBridge } from '../lib/context/StoryblokContext'
import { isInStoryblokEditor } from '../lib/utils/isInStoryblokEditor'
import { PREFIXED_STORYBLOK_LANGUAGES } from '../../config/storyblok/languages'

import PageTemplate from '../templates/PageTemplate'
import ProductTemplate from '../templates/ProductTemplate'
import ProductFactsFeaturesTemplate from '../templates/ProductFactsFeaturesTemplate'
import BlogPostTemplate from '../templates/BlogPostTemplate'

const STORYBLOK_API_URL = 'https://api.storyblok.com/v2/cdn/stories'
const PREVIEW_TOKEN = process.env.GATSBY_STORYBLOK_PREVIEW_TOKEN

const BLOG_COMPONENTS = [
  'CompanyInsights',
  'CustomerStories',
  'UseCase',
  'ProductNews',
  'PdfKnowledge',
]

const RESOLVE_RELATIONS = [
  'RecommendedArticlesTeaser.recommendedArticles',
  'CompanyInsights.author',
  'CustomerStories.usedProducts',
  'Product.operationsSystems',
  'Product.productVariant',
  'UseCase.usedProducts',
  'ProductTeaser.products',
].join(',')

interface StoryblokStory {
  id: number
  uuid: string
  name: string
  slug: string
  full_slug: string
  default_full_slug?: string
  first_published_at?: string
  published_at?: string
  lang?: string
  translated_slugs?: { lang: string; name: string; path: string }[]
  content: {
    component?: string
    [key: string]: any
  }
  [key: string]: any
}

function parseUrl(): { slug: string; language: string | null } {
  const path = window.location.pathname.replace(/^\//, '').replace(/\/$/, '')
  for (const lang of PREFIXED_STORYBLOK_LANGUAGES) {
    if (path === lang || path.startsWith(`${lang}/`)) {
      return { slug: path.slice(lang.length + 1) || 'home', language: lang }
    }
  }
  return { slug: path || 'home', language: null }
}

function storyToPageContext(story: StoryblokStory) {
  const defaultSlug = story.default_full_slug || story.full_slug

  return {
    ...story,
    field_component: story.content.component,
    field_pageTitle_string: story.content.pageTitle || story.content.title || story.name,
    field_pageDescription_string: story.content.pageDescription || story.content.introText || '',
    field_disableIndexing_boolean: story.content.disableIndexing || false,
    lang: story.lang || 'default',
    translated_slugs: story.translated_slugs || [],
    default_full_slug: defaultSlug,
    defaultSlug: defaultSlug,
  }
}

function getTemplateForComponent(componentType: string | undefined) {
  if (!componentType) return null

  if (BLOG_COMPONENTS.includes(componentType)) {
    return BlogPostTemplate
  }

  switch (componentType) {
    case 'page':
      return PageTemplate
    case 'Product':
      return ProductTemplate
    case 'ProductFactsFeatures':
      return ProductFactsFeaturesTemplate
    default:
      return null
  }
}

export function useStoryblokDraft(): { story: StoryblokStory | null; loading: boolean } {
  const [story, setStory] = useState<StoryblokStory | null>(null)
  const [loading, setLoading] = useState(false)
  const [isEditor, setIsEditor] = useState(false)
  const bridge = useStoryblokBridge()

  useEffect(() => {
    if (typeof window === 'undefined') return
    setIsEditor(isInStoryblokEditor())
  }, [])

  const fetchStory = useCallback(async () => {
    if (!PREVIEW_TOKEN) return

    try {
      setLoading(true)
      const { slug, language } = parseUrl()
      const params = new URLSearchParams({
        token: PREVIEW_TOKEN,
        version: 'draft',
        resolve_relations: RESOLVE_RELATIONS,
      })

      if (language) {
        params.set('language', language)
      }

      const response = await fetch(`${STORYBLOK_API_URL}/${slug}?${params.toString()}`)
      if (!response.ok) return

      const data = await response.json()
      setStory(data.story)
    } catch {
      // Draft not found — fall through to normal 404
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isEditor) {
      fetchStory()
    }
  }, [isEditor, fetchStory])

  useEffect(() => {
    if (bridge && story) {
      bridge.on(['input'], (event: any) => {
        if (event.story && event.story.uuid === story.uuid) {
          if (typeof event.story.content === 'string') {
            event.story.content = JSON.parse(event.story.content)
          }
          setStory(event.story)
        }
      })
    }
  }, [bridge, story])

  return { story, loading: isEditor && loading }
}

export const StoryblokDraftPreview = ({ story }: { story: StoryblokStory }) => {
  const Template = getTemplateForComponent(story.content.component)

  if (!Template) {
    return (
      <DefaultLayout>
        <div style={{ padding: '100px 20px', textAlign: 'center' }}>
          <h1>Unknown content type</h1>
          <p>No template found for component type: <code>{story.content.component}</code></p>
        </div>
      </DefaultLayout>
    )
  }

  const pageContext = storyToPageContext(story)
  return <Template pageContext={pageContext as any} />
}
